import { useFormFocusState } from 'base-components/BaseForm/BaseForm';
import BaseTooltip from 'base-components/BaseTooltip/BaseTooltip';
import { isFunction } from 'lodash';
import { DropDown } from 'models/api/dropDown';
import { useCallback, useMemo, useRef, useState } from 'react';
import { RefCallBack, useFormContext } from 'react-hook-form';
import Select, { ActionMeta, components, ControlProps, GroupBase, SingleValue } from 'react-select';
import { FilterOptionOption } from 'react-select/dist/declarations/src/filters';
import { ClassNamesConfig, StylesConfig } from 'react-select/dist/declarations/src/styles';
import { FormFeedback, Label } from 'reactstrap';
import 'scss/Main.scss';
import { v4 as uuidv4 } from 'uuid';
import './BaseDropDownList.scss';

export type BaseDropDownListProps = {
    name?: string;
    items: DropDown[];
    value?: string;
    label?: string;
    setValue: (value: DropDown) => void;
    isLoading?: boolean;
    isShowId?: boolean;
    disabled?: boolean;
    onlyListValue?: boolean;
    required?: boolean;
    invalid?: boolean;
    isClearable?: boolean;
    error?: string;
    isFloating?: boolean;
    isSearchable?: boolean;
    menuPortalTarget?: HTMLElement | null;
    refCallback?: RefCallBack;
};

const BaseDropDownList = ({
    name,
    items,
    label,
    value,
    setValue,
    isLoading: isFetching = false,
    isShowId = false,
    disabled = false,
    onlyListValue = false,
    required = false,
    isClearable = true,
    isSearchable = true,
    invalid,
    error,
    isFloating = true,
    refCallback,
    menuPortalTarget = document.body,
}: BaseDropDownListProps) => {
    const [isFocus, setFocus] = useState(false);
    const methods = useFormContext();
    const defaultName = useMemo(() => name ?? uuidv4(), [name]);
    const [isListValue, setIsListValue] = useState(true);
    const formFocusContext = useFormFocusState();

    const controlClass = useMemo(() => (invalid ? 'form-select__input_invalid' : ''), [invalid]);
    const controlTestId = useMemo(
        () => (invalid ? 'select-component-invalid' : 'select-component'),
        [invalid]
    );

    const handleChange = (newValue: SingleValue<DropDown>, actionMeta: ActionMeta<DropDown>) => {
        setIsListValue(false);
        methods && methods.clearErrors(defaultName);
        setValue({ ...newValue });
    };

    const formatOptionLabel = ({ id, label: icon, name: label }: any) => {
        return (
            <span className="react-select-option">
                {isShowId ? id : ''} {icon || label}
            </span>
        );
    };

    type IsMulti = false;

    const selectStyle: StylesConfig<DropDown, IsMulti> = {
        option: (provided, state) => ({
            ...provided,
            backgroundColor: '',
        }),
        control: (provided) => ({
            ...provided,
            borderColor: '',
            '&:hover': {
                border: '',
            },
            backgroundColor: '',
        }),
    };

    const classNames: ClassNamesConfig<DropDown, false, GroupBase<DropDown>> = {
        menuList: ({ className }) =>
            `${className ?? ''} custom-scrollbar base-dropdown-list__menu-list`,
        option: ({ className, isFocused }) =>
            `${className ?? ''} base-dropdown-list__option ` +
            (isFocused ? 'base-dropdown-list__option_focused' : ''),
        menuPortal: () => 'base-dropdown-list__menu-portal',
        menu: ({ className }) => `${className ?? ''} base-dropdown-list__menu`,
        singleValue: ({ className }) => `${className ?? ''} base-dropdown-list__single-value`,
        control: ({ className, isDisabled, isFocused }) =>
            (className ?? '') +
            ' base-dropdown-list__control ' +
            (isDisabled ? 'disabled ' : '') +
            (isFocused ? 'base-dropdown-list__control_focused ' : ''),
        indicatorsContainer: () => 'base-dropdown-list__indicators',
    };

    // TODO: Разобраться с типами значений и идентификаторов в списках
    // eslint-disable-next-line eqeqeq
    const val = useMemo(() => items.find((x) => x.id == value) ?? { name: value }, [value, items]);

    const controlRef = useRef<HTMLDivElement | null>(null);

    const Control = useCallback(
        ({
            children,
            innerRef,
            ...props
        }: ControlProps<DropDown, boolean, GroupBase<DropDown>>) => (
            <>
                {label && (
                    <Label
                        check={false}
                        className={`${props.isDisabled ? 'disabled' : ''} ${
                            props.isFocused || val.id || val.name ? 'form-label__focus' : ''
                        }`}
                    >
                        {label}
                    </Label>
                )}
                <components.Control
                    {...props}
                    className={`form-select ${controlClass}`}
                    innerRef={(i) => {
                        controlRef.current = i;
                        if (isFunction(innerRef)) innerRef(i);
                        else return innerRef;
                    }}
                >
                    {children}
                </components.Control>

                <BaseTooltip
                    target={controlRef}
                    showOnlyIfOverflownRef={controlRef}
                    overflownElementClassName="base-dropdown-list__single-value"
                    container={menuPortalTarget ?? undefined}
                >
                    {val.name}
                </BaseTooltip>
            </>
        ),
        [controlClass, val]
    );

    const filterOptions = ({ data }: FilterOptionOption<DropDown>, input: string) => {
        if (input && data.name) {
            return data.name.toLowerCase().includes(input.toLowerCase());
        }
        return true;
    };

    return (
        <div className="position-relative" data-testid={controlTestId}>
            <Select<DropDown>
                data-testid="select-input"
                options={items}
                name={defaultName}
                menuPlacement="auto"
                isMulti={false}
                isDisabled={disabled}
                styles={selectStyle}
                isSearchable={isSearchable}
                isClearable={isClearable}
                value={val}
                isLoading={isFetching}
                formatOptionLabel={formatOptionLabel}
                filterOption={filterOptions}
                components={{
                    Control,
                }}
                className={`base-dropdown-list mb-3 ${isFloating && label ? 'form-floating' : ''}`}
                classNames={classNames}
                menuPortalTarget={menuPortalTarget}
                onChange={handleChange}
                onFocus={() => {
                    setFocus(true);
                }}
                onBlur={() => {
                    setFocus(false);
                    if ((onlyListValue && !isListValue) || (required && !value)) {
                        methods && methods.setError(defaultName, {});
                    }
                }}
                blurInputOnSelect
                openMenuOnFocus
                ref={(instance) => {
                    refCallback &&
                        refCallback({
                            ...instance,
                            focus: () => {
                                formFocusContext.setFocusFieldRef(instance);
                                setTimeout(() => instance?.focus(), 1);
                            },
                        });
                }}
            />
            {!isFocus && (
                <FormFeedback className={error ? 'd-block' : ''} tooltip>
                    {error}
                </FormFeedback>
            )}
        </div>
    );
};

export default BaseDropDownList;
